import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Checkbox,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IReservationPrice, KURTAXE_CATEGORIES, reservationPriceSchema } from '../Interfaces/IReservationPrice';
import { useDeleteReservationPrice, useGetReservationPrice } from '../hooks/reservationsPrice';
import { checkPermission } from '../utils/permission';
import ReservationPriceEdit from './ReservationPriceEdit';
import KurtaxePanel from './KurtaxePanel';
import { BrandedDataGridOverlay } from '../core/BrandedDataGridOverlay';
import { useAlert } from '../core/AlertContext';
import RowActionsMenu from '../core/RowActionsMenu';
import AuditInfoDialog from '../core/AuditInfoDialog';
import DetailScreenHeader from '../core/DetailScreenHeader';
import { EVENT_DIALOG_MAX_WIDTH, EVENT_DIALOG_PAPER_SX } from '../calendar/dialogLayout';
import PageHeader from '../core/PageHeader';
import PvtaCard from '../core/PvtaCard';
import { primaryButtonSx, secondaryButtonSx, dangerButtonSx } from '../core/pvtaStyles';

const emptyReservationPrice = reservationPriceSchema.parse({});

const formatDateTime = (value?: string | null): string => {
    if (!value) {
        return '-';
    }
    return new Intl.DateTimeFormat('de-DE', {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(new Date(value));
};

const getKurtaxeCategoryLabel = (value?: string | null): string =>
    KURTAXE_CATEGORIES.find((option) => option.value === (value ?? ''))?.label ?? '-';

const getLastModifiedBy = (reservationPrice: IReservationPrice): string =>
    reservationPrice.last_modified_by?.displayName ?? reservationPrice.created_by?.displayName ?? '-';

const getLastModifiedAt = (reservationPrice: IReservationPrice): string =>
    formatDateTime(reservationPrice.last_modified_at ?? reservationPrice.updated_at ?? reservationPrice.created_at ?? null);

export const ReservationPriceOverview: React.FC = () => {
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [selectedPrice, setSelectedPrice] = useState<IReservationPrice | null>(null);
    const [deleteCandidate, setDeleteCandidate] = useState<IReservationPrice | null>(null);
    const [infoCandidate, setInfoCandidate] = useState<IReservationPrice | null>(null);
    const { reservationPriceArray, isLoadingReservationPriceArray } = useGetReservationPrice({ includeDeleted });
    const { deleteReservationPrice, isDeletingReservationPrice } = useDeleteReservationPrice();
    const { showAlert } = useAlert();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleRowClick = (params: GridRowParams<IReservationPrice>) => {
        setSelectedPrice(params.row);
    };

    const handleDelete = async () => {
        if (!deleteCandidate) {
            return;
        }
        try {
            await deleteReservationPrice(deleteCandidate.id);
            showAlert({ text: 'Übernachtungspauschale erfolgreich gelöscht.' });
            setDeleteCandidate(null);
        } catch {
            showAlert({ text: 'Fehler beim Löschen der Übernachtungspauschale.', severity: 'error' });
        }
    };

    const columns: GridColDef<IReservationPrice>[] = [
        { field: 'name', headerName: 'Name', flex: 1.1, minWidth: 180 },
        { field: 'price', headerName: 'Preis', flex: 0.5, minWidth: 110 },
        {
            field: 'kurtaxe_category',
            headerName: 'Kurtaxe-Stufe',
            flex: 0.8,
            minWidth: 150,
            valueGetter: (_value, row) => getKurtaxeCategoryLabel(row.kurtaxe_category),
        },
        {
            field: 'last_modified_by',
            headerName: 'Zuletzt bearbeitet von',
            flex: 0.9,
            minWidth: 190,
            valueGetter: (_value, row) => getLastModifiedBy(row),
        },
        {
            field: 'last_modified_at',
            headerName: 'Zuletzt bearbeitet am',
            flex: 0.9,
            minWidth: 180,
            valueGetter: (_value, row) => getLastModifiedAt(row),
        },
        {
            field: 'deleted_at',
            headerName: 'Gelöscht am',
            flex: 0.8,
            minWidth: 160,
            valueGetter: (_value, row) => formatDateTime(row.deleted_at),
        },
        {
            field: 'actions',
            headerName: 'Aktionen',
            sortable: false,
            filterable: false,
            minWidth: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<IReservationPrice>) => (
                <RowActionsMenu
                    items={[
                        {
                            label: 'Bearbeiten',
                            icon: <EditOutlinedIcon fontSize="small" />,
                            onClick: () => setSelectedPrice(params.row),
                        },
                        {
                            label: 'Löschen',
                            icon: <DeleteOutlineIcon fontSize="small" />,
                            disabled: Boolean(params.row.deleted_at),
                            onClick: () => setDeleteCandidate(params.row),
                        },
                        {
                            label: 'Weitere Informationen',
                            icon: <InfoOutlinedIcon fontSize="small" />,
                            onClick: () => setInfoCandidate(params.row),
                        },
                    ]}
                />
            ),
        },
    ];

    if (isLoadingReservationPriceArray) {
        return (
            <Box sx={{ py: { xs: 3, md: 4 } }}>
                <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
                    <Skeleton variant="text" width={230} height={44} sx={{ mb: 3 }} />
                    <Stack spacing={1}>
                        <Skeleton variant="rounded" height={48} />
                        {[0, 1, 2, 3].map((item) => (
                            <Skeleton key={item} variant="rounded" height={52} />
                        ))}
                    </Stack>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ py: { xs: 3, md: 4 } }}>
            <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
                <PageHeader title="Kosten" subtitle="Übernachtungspreise verwalten." />
                {checkPermission(2) && <KurtaxePanel />}
                <PvtaCard large sx={{ p: { xs: 2, sm: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={1.5}
                    sx={{ mb: 2, minWidth: 0 }}
                >
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={includeDeleted}
                                onChange={(event) => setIncludeDeleted(event.target.checked)}
                            />
                        )}
                        label="Gelöschte anzeigen"
                    />
                    <Button
                        startIcon={<AddIcon />}
                        onClick={() => setSelectedPrice(emptyReservationPrice)}
                        sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
                    >
                        Neu anlegen
                    </Button>
                </Stack>
                {!isMobile ? (
                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                        <DataGrid
                            rows={reservationPriceArray}
                            columns={columns}
                            disableRowSelectionOnClick
                            onRowClick={handleRowClick}
                            getRowId={(row) => row.id}
                            autoHeight
                            pageSizeOptions={[10, 25, 50]}
                            slots={{
                                noRowsOverlay: BrandedDataGridOverlay,
                                noResultsOverlay: BrandedDataGridOverlay,
                            }}
                            sx={{
                                border: 0,
                                '& .MuiDataGrid-row': {
                                    cursor: 'pointer',
                                },
                            }}
                        />
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {reservationPriceArray.map((price) => (
                            <Card key={price.id} variant="outlined">
                                <CardActionArea onClick={() => setSelectedPrice(price)}>
                                    <CardContent>
                                        <Stack spacing={1}>
                                            <Stack direction="row" justifyContent="space-between" spacing={2}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {price.name}
                                                </Typography>
                                                <Typography variant="subtitle2">
                                                    {price.price}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary">
                                                Zuletzt bearbeitet von {getLastModifiedBy(price)} am {getLastModifiedAt(price)}
                                            </Typography>
                                            {price.deleted_at && (
                                                <Typography variant="caption" color="error.main">
                                                    Gelöscht am {formatDateTime(price.deleted_at)}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        ))}
                    </Stack>
                )}
                </PvtaCard>
                <Dialog
                    open={selectedPrice !== null}
                    onClose={() => setSelectedPrice(null)}
                    fullWidth
                    maxWidth={EVENT_DIALOG_MAX_WIDTH}
                    fullScreen={isMobile}
                    PaperProps={EVENT_DIALOG_PAPER_SX}
                >
                    <DetailScreenHeader
                        title={selectedPrice?.id && selectedPrice.id > 0
                            ? 'Übernachtungstarif bearbeiten'
                            : 'Übernachtungstarif anlegen'}
                        onClose={() => setSelectedPrice(null)}
                    />
                    <DialogContent sx={{ pt: 2 }}>
                        {selectedPrice && (
                            <ReservationPriceEdit
                                initialPrice={selectedPrice}
                                onClose={() => setSelectedPrice(null)}
                                showHeading={false}
                            />
                        )}
                    </DialogContent>
                </Dialog>
                <Dialog open={deleteCandidate !== null} onClose={() => setDeleteCandidate(null)}>
                    <DialogTitle>Übernachtungstarif löschen?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2">
                            Soll der Übernachtungstarif &quot;{deleteCandidate?.name}&quot; wirklich gelöscht werden?
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Hinweis: Der Eintrag wird archiviert und kann über den Filter wieder angezeigt werden.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setDeleteCandidate(null)} sx={secondaryButtonSx}>Abbrechen</Button>
                        <Button sx={dangerButtonSx} disabled={isDeletingReservationPrice} onClick={() => { void handleDelete(); }}>
                            Löschen
                        </Button>
                    </DialogActions>
                </Dialog>
                <AuditInfoDialog
                    open={infoCandidate !== null}
                    onClose={() => setInfoCandidate(null)}
                    title="Audit-Informationen"
                    createdBy={infoCandidate?.created_by}
                    createdAt={infoCandidate?.created_at}
                    updatedBy={infoCandidate?.updated_by}
                    updatedAt={infoCandidate?.updated_at}
                    deletedBy={infoCandidate?.deleted_by}
                    deletedAt={infoCandidate?.deleted_at}
                />
            </Container>
        </Box>
    );
};

export default ReservationPriceOverview;
